import { createSignal, onMount, createEffect, For, Show } from "solid-js";
import { api, type EmailVerificationLinkInfo } from "../shared";
import { Pagination } from "./Pagination";

interface Props {
  organizationId: string;
  onGenerate: () => void;
}

export function VerificationLinksSection(props: Props) {
  const [links, setLinks] = createSignal<EmailVerificationLinkInfo[]>([]);
  const [total, setTotal] = createSignal(0);
  const [page, setPage] = createSignal(1);
  const [perPage, setPerPage] = createSignal(20);
  const [loading, setLoading] = createSignal(false);

  async function load() {
    setLoading(true);
    try {
      const result = await api.emailVerificationLinks.list({
        organizationId: props.organizationId,
        limit: perPage(),
        offset: (page() - 1) * perPage(),
      });
      setLinks(result.items || []);
      setTotal(result.total);
    } catch (e: any) {
      setLinks([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  onMount(load);
  createEffect(() => { page(); perPage(); load(); });

  async function doRevoke(id: string) {
    try {
      await api.emailVerificationLinks.revoke(id);
      load();
    } catch (e: any) {
      /* silently fail */
    }
  }

  function isExpired(expiresAt: string) {
    return new Date(expiresAt) < new Date();
  }

  return (
    <div class="space-y-3">
      <div class="flex items-center justify-between">
        <h3 class="text-sm font-semibold text-gray-400 uppercase tracking-wide">Verification Links ({total()})</h3>
        <button onClick={props.onGenerate} class="text-xs px-3 py-1 bg-cyan-900/30 border border-cyan-700 text-cyan-300 rounded hover:bg-cyan-900/50 cursor-pointer">+ Generate Link</button>
      </div>
      <div class="border border-gray-800 rounded-lg overflow-x-auto max-h-80 overflow-y-auto">
        <table class="w-full text-sm">
          <thead class="bg-gray-900 text-gray-400 sticky top-0">
            <tr>
              <th class="text-left px-4 py-3 font-medium">User</th>
              <th class="text-left px-4 py-3 font-medium">Created</th>
              <th class="text-left px-4 py-3 font-medium">Expires</th>
              <th class="text-left px-4 py-3 font-medium">Status</th>
              <th class="text-right px-4 py-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody class="divide-y divide-gray-800">
            <For each={links()}>
              {(link) => (
                <tr class="hover:bg-gray-900/50">
                  <td class="px-4 py-3 text-gray-200">{link.userEmail}</td>
                  <td class="px-4 py-3 text-gray-500 text-xs">{new Date(link.createdAt).toLocaleString()}</td>
                  <td class="px-4 py-3 text-gray-500 text-xs">{new Date(link.expiresAt).toLocaleString()}</td>
                  <td class="px-4 py-3">
                    {link.consumedAt
                      ? <span class="text-xs text-gray-500">Used {new Date(link.consumedAt).toLocaleString()}</span>
                      : isExpired(link.expiresAt)
                        ? <span class="text-xs text-gray-500">Expired</span>
                        : <span class="text-xs text-emerald-400">Active</span>
                    }
                  </td>
                  <td class="px-4 py-3 text-right space-x-1">
                    <Show when={!link.consumedAt && !isExpired(link.expiresAt)}>
                      <button onClick={() => doRevoke(link.id)} class="text-xs px-2 py-0.5 bg-red-900/30 border border-red-700 text-red-300 rounded hover:bg-red-900/50 cursor-pointer">Revoke</button>
                    </Show>
                    <button onClick={() => { navigator.clipboard.writeText(`${window.location.origin}/verify-email?token=${encodeURIComponent(link.token)}`); }} class="text-xs px-2 py-0.5 bg-gray-800 text-gray-300 rounded hover:bg-gray-700 cursor-pointer">Copy</button>
                  </td>
                </tr>
              )}
            </For>
          </tbody>
        </table>
      </div>
      <Pagination
        page={page()}
        totalPages={Math.ceil(total() / perPage())}
        perPage={perPage()}
        total={total()}
        loading={loading()}
        onPageChange={setPage}
        onPerPageChange={(pp) => { setPage(1); setPerPage(pp); }}
      />
    </div>
  );
}
